import React, { useState, useEffect, useCallback } from "react";
import { netflixMailAPI } from "../services/api";
import toast from "react-hot-toast";
import { Plus, Pencil, Trash2, KeyRound, Eye, X, Mail, Webhook, Copy, Check } from "lucide-react";

interface Mailbox {
  _id: string;
  email: string;
  label: string;
  mode: "imap" | "webhook";
  host: string;
  port: number;
  secure: boolean;
  webhookSecret?: string;
  createdAt: string;
  updatedAt: string;
}

interface FetchCodeResult {
  found: boolean;
  message?: string;
  subject?: string;
  from?: string;
  folder?: string;
  receivedAt?: string | null;
  code?: string | null;
  snippet?: string;
}

const INPUT = "w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-coffee-400";
// Webhook được gọi từ dịch vụ ngoài (Zoho...) chạy trên cloud — luôn phải là domain
// thật, không được dùng REACT_APP_API_URL vì lúc test ở local sẽ ra localhost,
// Zoho không thể gọi vào máy cá nhân được.
const WEBHOOK_API_BASE = "https://api.23homestay.vn/api";

interface ProviderPreset { label: string; host: string; port: number; secure: boolean; hint: string; emailHint: string; }

const PROVIDERS: Record<string, ProviderPreset> = {
  gmail: {
    label: "Gmail",
    host: "imap.gmail.com", port: 993, secure: true,
    emailHint: "vidu@gmail.com",
    hint: "Vào tài khoản Google → Bảo mật → bật Xác minh 2 bước → Mật khẩu ứng dụng để lấy chuỗi 16 ký tự này.",
  },
  zoho: {
    label: "Zoho Mail",
    host: "imap.zoho.com", port: 993, secure: true,
    emailHint: "vidu@zoho.com",
    hint: "Vào Zoho Mail → biểu tượng bánh răng → Mail Accounts → chọn tài khoản → bật \"IMAP Access\". Sau đó vào My Account → Security → App Passwords → tạo mật khẩu ứng dụng để dùng ở đây. (Lưu ý: gói Zoho Mail miễn phí KHÔNG có IMAP — nếu vậy hãy chọn chế độ \"Webhook (Zoho free)\" ở trên thay vì IMAP.)",
  },
  outlook: {
    label: "Outlook / Microsoft 365",
    host: "outlook.office365.com", port: 993, secure: true,
    emailHint: "vidu@outlook.com",
    hint: "Vào tài khoản Microsoft → Bảo mật → bật Xác minh 2 bước → App passwords để tạo mật khẩu ứng dụng.",
  },
  custom: {
    label: "Khác (tự nhập IMAP server)",
    host: "", port: 993, secure: true,
    emailHint: "vidu@tenmien.com",
    hint: "Nhập đúng địa chỉ IMAP server + port do nhà cung cấp mail của bạn cấp (thường tìm trong phần cài đặt IMAP/POP của mail đó).",
  },
};

function detectProvider(host?: string): string {
  if (!host) return "gmail";
  const found = Object.entries(PROVIDERS).find(([key, p]) => key !== "custom" && p.host === host);
  return found ? found[0] : "custom";
}

function buildDelugeScript(webhookUrl: string, secret: string): string {
  return `messageDetails = zoho.mail.getMessage(mail_messageId);
info = Map();
info.put("secret", "${secret}");
info.put("subject", messageDetails.get("SUBJECT"));
info.put("from", messageDetails.get("FROM"));
info.put("content", messageDetails.get("CONTENT"));
response = invokeUrl
[
	url: "${webhookUrl}"
	type: POST
	body: info.toString()
	headers: {"Content-Type": "application/json"}
];`;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-50"
    >
      {copied ? <><Check size={11} className="text-emerald-600" /> Đã copy</> : <><Copy size={11} /> Copy</>}
    </button>
  );
}

function WebhookSetupModal({ mailbox, onClose }: { mailbox: Mailbox; onClose: () => void }) {
  const webhookUrl = `${WEBHOOK_API_BASE}/netflix-mail/webhook/${mailbox._id}`;
  const script = buildDelugeScript(webhookUrl, mailbox.webhookSecret || "");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-zinc-900">Cấu hình Webhook cho {mailbox.email}</h2>
          <button onClick={onClose}><X size={20} className="text-zinc-400" /></button>
        </div>

        <div className="text-sm text-zinc-600 space-y-2">
          <p>Mail này không dùng IMAP — thay vào đó, tạo 1 <strong>Filter</strong> trên Zoho Mail để tự động gửi mã Netflix về hệ thống mỗi khi có mail mới:</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-500">
            <li>Vào Zoho Mail → Cài đặt (bánh răng) → <strong>Filters</strong> → Tạo filter mới</li>
            <li>Điều kiện: <strong>From contains netflix.com</strong></li>
            <li>Action: chọn <strong>Custom function</strong> (hoặc &quot;Custom function (Creator)&quot;)</li>
            <li>Dán đoạn script Deluge bên dưới vào ô function → Save</li>
          </ol>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold text-zinc-500">Script Deluge (dán nguyên vào Zoho)</label>
            <CopyButton text={script} />
          </div>
          <pre className="bg-zinc-900 text-zinc-100 text-[11px] rounded-xl p-3 overflow-x-auto whitespace-pre">{script}</pre>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
          Webhook URL và secret ở trên là <strong>riêng cho mail này</strong> — không dùng chung cho mail khác. Nếu xoá và tạo lại mail này, phải cập nhật lại script trên Zoho.
        </div>

        <div className="flex justify-end pt-1">
          <button onClick={onClose} className="px-4 py-2 bg-coffee-600 text-white rounded-lg text-sm font-semibold">Đã hiểu</button>
        </div>
      </div>
    </div>
  );
}

function MailboxFormModal({ initial, onClose, onSaved }: {
  initial?: Mailbox | null; onClose: () => void; onSaved: (m: Mailbox) => void;
}) {
  const [email, setEmail] = useState(initial?.email || "");
  const [label, setLabel] = useState(initial?.label || "");
  const [connMode, setConnMode] = useState<"imap" | "webhook">(initial?.mode || "imap");
  const [appPassword, setAppPassword] = useState("");
  const [provider, setProvider] = useState(() => detectProvider(initial?.host));
  const [customHost, setCustomHost] = useState(detectProvider(initial?.host) === "custom" ? (initial?.host || "") : "");
  const [customPort, setCustomPort] = useState(String(initial?.port || 993));
  const [saving, setSaving] = useState(false);

  const preset = PROVIDERS[provider];
  const isCustom = provider === "custom";
  const isWebhook = connMode === "webhook";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Vui lòng nhập email"); return; }
    if (!isWebhook && !initial && !appPassword.trim()) { toast.error("Vui lòng nhập App Password"); return; }
    if (!isWebhook && isCustom && !customHost.trim()) { toast.error("Vui lòng nhập IMAP server"); return; }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = { email, label, mode: connMode };
      if (!isWebhook) {
        payload.host = isCustom ? customHost.trim() : preset.host;
        payload.port = isCustom ? Number(customPort) || 993 : preset.port;
        payload.secure = preset.secure;
        if (appPassword.trim()) payload.appPassword = appPassword;
      }
      const res = initial
        ? await netflixMailAPI.update(initial._id, payload)
        : await netflixMailAPI.create(payload);
      toast.success(initial ? "Đã cập nhật" : "Đã thêm mail");
      onSaved(res.data.data); onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Lỗi khi lưu");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-zinc-900">{initial ? "Sửa mail" : "Thêm mail Netflix"}</h2>
          <button onClick={onClose}><X size={20} className="text-zinc-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Nhãn (tuỳ chọn)</label>
            <input className={INPUT} value={label} onChange={e => setLabel(e.target.value)} placeholder='VD: "Slot 1 - Anh A"' />
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Chế độ kết nối *</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setConnMode("imap")}
                className={`px-3 py-2 rounded-lg text-xs font-semibold border ${connMode === "imap" ? "bg-coffee-600 text-white border-coffee-600" : "bg-white text-zinc-500 border-zinc-200"}`}>
                IMAP (Gmail, Outlook...)
              </button>
              <button type="button" onClick={() => setConnMode("webhook")}
                className={`px-3 py-2 rounded-lg text-xs font-semibold border ${connMode === "webhook" ? "bg-coffee-600 text-white border-coffee-600" : "bg-white text-zinc-500 border-zinc-200"}`}>
                Webhook (Zoho free...)
              </button>
            </div>
            {isWebhook && (
              <p className="text-[11px] text-zinc-400 mt-1.5">
                Dùng khi mail không hỗ trợ IMAP (VD Zoho Mail gói miễn phí). Sau khi lưu, hệ thống sẽ hiện script để bạn dán vào Filter trên mail đó — không cần App Password.
              </p>
            )}
          </div>

          {!isWebhook && (
            <>
              <div>
                <label className="block text-xs font-medium text-zinc-500 mb-1">Nhà cung cấp mail *</label>
                <select className={INPUT + " bg-white"} value={provider} onChange={e => setProvider(e.target.value)}>
                  {Object.entries(PROVIDERS).map(([key, p]) => (
                    <option key={key} value={key}>{p.label}</option>
                  ))}
                </select>
              </div>
              {isCustom && (
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-zinc-500 mb-1">IMAP server *</label>
                    <input className={INPUT} value={customHost} onChange={e => setCustomHost(e.target.value)} placeholder="imap.tenmien.com" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Port</label>
                    <input className={INPUT} type="number" value={customPort} onChange={e => setCustomPort(e.target.value)} placeholder="993" />
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Email *</label>
            <input className={INPUT} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={isWebhook ? "vidu@zoho.com" : preset.emailHint} required />
          </div>

          {!isWebhook && (
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                App Password {initial ? "(để trống nếu không đổi)" : "*"}
              </label>
              <input className={INPUT} value={appPassword} onChange={e => setAppPassword(e.target.value)} placeholder="xxxx xxxx xxxx xxxx" />
              <p className="text-[11px] text-zinc-400 mt-1">{preset.hint}</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-zinc-200 rounded-lg text-sm text-zinc-600">Huỷ</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-coffee-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
              {saving ? "Đang lưu..." : initial ? "Cập nhật" : "Thêm"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CodeDetailModal({ mailbox, result, onClose }: {
  mailbox: Mailbox; result: FetchCodeResult; onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-3 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-zinc-900">Mã Netflix mới nhất</h2>
          <button onClick={onClose}><X size={20} className="text-zinc-400" /></button>
        </div>
        <div className="text-sm space-y-1.5 text-zinc-600">
          <p><span className="font-semibold text-zinc-800">Mail:</span> {mailbox.email}</p>
          <p><span className="font-semibold text-zinc-800">Tiêu đề:</span> {result.subject || "—"}</p>
          <p><span className="font-semibold text-zinc-800">Từ:</span> {result.from || "—"}</p>
          <p><span className="font-semibold text-zinc-800">Nhận lúc:</span> {result.receivedAt ? new Date(result.receivedAt).toLocaleString("vi-VN") : "—"}</p>
          {result.folder && <p><span className="font-semibold text-zinc-800">Folder:</span> {result.folder}</p>}
          <p>
            <span className="font-semibold text-zinc-800">Mã trích được:</span>{" "}
            <span className="font-mono font-bold text-red-600 text-lg">{result.code || "(không nhận diện được, đọc nội dung bên dưới)"}</span>
          </p>
        </div>
        <div className="bg-zinc-50 border border-zinc-100 rounded-xl p-3 text-xs text-zinc-500 whitespace-pre-wrap max-h-52 overflow-y-auto">
          {result.snippet || "Không có nội dung"}
        </div>
      </div>
    </div>
  );
}

export default function NetflixMailPage() {
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingMailbox, setEditingMailbox] = useState<Mailbox | null>(null);
  const [fetchingId, setFetchingId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, FetchCodeResult>>({});
  const [viewingResult, setViewingResult] = useState<{ mailbox: Mailbox; result: FetchCodeResult } | null>(null);
  const [webhookSetupFor, setWebhookSetupFor] = useState<Mailbox | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await netflixMailAPI.getAll(); setMailboxes(res.data.data); }
    catch { toast.error("Không tải được danh sách mail"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (m: Mailbox) => {
    if (!window.confirm(`Xoá mail "${m.label || m.email}"?`)) return;
    try {
      await netflixMailAPI.delete(m._id);
      setMailboxes(prev => prev.filter(x => x._id !== m._id));
      toast.success("Đã xoá");
    } catch { toast.error("Lỗi khi xoá"); }
  };

  const handleFetchCode = async (m: Mailbox) => {
    setFetchingId(m._id);
    try {
      const res = await netflixMailAPI.fetchCode(m._id);
      setResults(prev => ({ ...prev, [m._id]: res.data.data }));
    } catch (err: any) {
      setResults(prev => ({ ...prev, [m._id]: { found: false, message: err?.response?.data?.message || "Lỗi kết nối" } }));
    } finally {
      setFetchingId(null);
    }
  };

  const handleCreated = (m: Mailbox) => {
    setMailboxes(prev => [m, ...prev]);
    if (m.mode === "webhook") setWebhookSetupFor(m);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Quản lý mail Netflix</h1>
          <p className="text-sm text-zinc-500 mt-1">Danh sách mail quản lý Netflix — lấy mã đăng nhập mới nhất từng hộp thư</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-coffee-600 text-white rounded-lg text-sm font-semibold hover:bg-coffee-700">
          <Plus size={16} /> Thêm mail
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">Đang tải...</div>
      ) : mailboxes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-zinc-400 gap-3">
          <Mail size={40} className="text-zinc-300" />
          <p className="font-medium">Chưa có mail nào</p>
          <button onClick={() => setShowCreate(true)} className="text-sm text-coffee-600 hover:underline">+ Thêm mail đầu tiên</button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-zinc-100 bg-zinc-50">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wide">Nhãn</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wide">Email</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wide">Mã mới nhất</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold text-zinc-500 uppercase tracking-wide">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {mailboxes.map(m => {
                const r = results[m._id];
                const isWebhook = m.mode === "webhook";
                return (
                  <tr key={m._id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-semibold text-zinc-800">{m.label || "—"}</td>
                    <td className="px-4 py-3 text-sm text-zinc-600">
                      {m.email}
                      <span className={`ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${isWebhook ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>
                        {isWebhook ? "Webhook" : "IMAP"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {!r ? (
                        <span className="text-xs text-zinc-300">Chưa lấy</span>
                      ) : r.found === false ? (
                        <span className="text-xs text-zinc-400">{r.message || "Không tìm thấy"}</span>
                      ) : (
                        <div>
                          <div className="font-mono font-bold text-red-600">{r.code || "(không rõ)"}</div>
                          <div className="text-[11px] text-zinc-400">{r.receivedAt ? new Date(r.receivedAt).toLocaleString("vi-VN") : ""}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => handleFetchCode(m)} disabled={fetchingId === m._id}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-coffee-50 text-coffee-700 border border-coffee-200 rounded-lg hover:bg-coffee-100 font-medium disabled:opacity-50">
                          <KeyRound size={12} /> {fetchingId === m._id ? "Đang lấy..." : "Lấy code"}
                        </button>
                        {r?.found && (
                          <button onClick={() => setViewingResult({ mailbox: m, result: r })}
                            className="p-1.5 text-zinc-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg" title="Xem chi tiết">
                            <Eye size={15} />
                          </button>
                        )}
                        {isWebhook && (
                          <button onClick={() => setWebhookSetupFor(m)} className="p-1.5 text-zinc-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg" title="Xem cấu hình Webhook">
                            <Webhook size={15} />
                          </button>
                        )}
                        <button onClick={() => setEditingMailbox(m)} className="p-1.5 text-zinc-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg">
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => handleDelete(m)} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <MailboxFormModal onClose={() => setShowCreate(false)} onSaved={handleCreated} />
      )}
      {editingMailbox && (
        <MailboxFormModal initial={editingMailbox} onClose={() => setEditingMailbox(null)}
          onSaved={updated => setMailboxes(prev => prev.map(x => x._id === updated._id ? updated : x))} />
      )}
      {viewingResult && (
        <CodeDetailModal mailbox={viewingResult.mailbox} result={viewingResult.result} onClose={() => setViewingResult(null)} />
      )}
      {webhookSetupFor && (
        <WebhookSetupModal mailbox={webhookSetupFor} onClose={() => setWebhookSetupFor(null)} />
      )}
    </div>
  );
}
